import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { StorefrontProduct } from '../api/client';
import { CheckoutModal } from '../components/CheckoutModal';
import { OptionPills, QuantityStepper } from '../elements/render/PurchaseControls';
import { productOptions, valueInStock, variantFor, variantValues } from '../elements/render/purchaseChoice';
import { isThemePreview } from '../api/previewVisit';
import { useCommerce, type Commerce } from './ShopCommerce';
import { cartMoney } from './cartMoney';
import './quickView.css';
import { maxQuantity, orderable, statedStock } from './variantStock';
import { shopWords, withCount } from './shopWords';
import { responsive } from '../elements/render/responsiveImage';
import { useShopNames } from './shopNames';

// Sleek's quick view: a product looked at from its card without leaving the page — its pictures one at a
// time, its name, price and stock, its words, its options and the button that adds it — in the same
// side panel the cart opens in, with the way on to the product's own page at its foot.

/** One variant of a product. */
type Variant = StorefrontProduct['variants'][number];

// Reads a stored description as plain words, its headings left out: they repeat the name the panel already shows.
const descriptionWords = (html: string | undefined): string =>
  (html ?? '').replace(/<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>/gi, ' ').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

// Picks the variant the panel opens on: the first one that can be ordered, else the first.
const firstOrderable = (product: StorefrontProduct): Variant | undefined =>
  product.variants.find((variant) => orderable(variant)) ?? product.variants[0];

// Draws the product's pictures one at a time, with the arrows and the "1/2" count between them.
const QuickGallery: React.FC<{ product: StorefrontProduct }> = ({ product }) => {
  const [at, setAt] = useState(0);
  const names = useShopNames();
  const images = product.images ?? [];
  const image = images[at];
  // Moves to the next or previous picture, round from the last to the first.
  const step = (by: number) => setAt((current) => (current + by + images.length) % images.length);
  return (
    <div className="xe-quick-gallery">
      <div className="xe-quick-media">{image ? <img {...responsive(image.src, '(max-width: 767px) 100vw, 560px')} alt={product.title} width={560} height={560} /> : null}</div>
      {images.length > 1 ? (
        <div className="xe-quick-steps">
          <button type="button" aria-label={names.previous} onClick={() => step(-1)}><ChevronLeft size={18} aria-hidden="true" /></button>
          <span aria-live="polite">{at + 1}/{images.length}</span>
          <button type="button" aria-label={names.next} onClick={() => step(1)}><ChevronRight size={18} aria-hidden="true" /></button>
        </div>
      ) : null}
    </div>
  );
};

// Draws what one variant costs, red beside its struck compare-at price when it is on sale.
const QuickPrice: React.FC<{ variant: Variant | undefined; commerce: Commerce }> = ({ variant, commerce }) => {
  // Spells a price in the store's currency and reading direction.
  const money = (raw: string) => cartMoney(Number(raw), commerce.currency, commerce.direction);
  if (!variant) return null;
  const sale = variant.compareAtPrice && Number(variant.compareAtPrice) > Number(variant.price);
  return (
    <p className="xe-quick-price" data-sale={sale || undefined}>
      <strong>{money(variant.price)}</strong>{sale ? <s>{money(variant.compareAtPrice ?? '0')}</s> : null}
    </p>
  );
};

// Draws the options, the quantity and the button that adds the chosen variant and opens the cart.
const QuickBuy: React.FC<{ product: StorefrontProduct; commerce: Commerce; variant: Variant | undefined; onPick: (variant: Variant) => void }> = ({ product, commerce, variant, onPick }) => {
  const [quantity, setQuantity] = useState(1);
  const picks = variant ? variantValues(product, variant) : [];
  const canOrder = orderable(variant) && !product.id.startsWith('sample-') && !isThemePreview();
  // 1. A pick that names no variant is ignored, so the panel never shows a price no variant has.
  const pick = (index: number, value: string) => {
    const next = variantFor(product, picks.map((current, at) => (at === index ? value : current)));
    if (next) onPick(next);
  };
  // 2. Adding closes this panel first, so the cart that opens is the only panel on the page.
  const add = () => {
    if (!variant) return;
    commerce.peek(null);
    commerce.add(product, variant.id, quantity);
  };
  return (
    <div className="xe-quick-buy">
      {productOptions(product).map((option, index) => (
        <OptionPills key={option.name} option={option} picked={picks[index] ?? ''} disabled={false}
          inStock={(value) => valueInStock(product, picks, index, value)} onPick={(value) => pick(index, value)} />
      ))}
      <div className="xe-quick-row">
        <QuantityStepper value={quantity} max={maxQuantity(variant)} disabled={!canOrder} onChange={setQuantity} name={product.title} />
        <button type="button" className="xe-quick-add" disabled={!canOrder} onClick={add}>{canOrder || !variant || orderable(variant) ? shopWords(commerce.language).addToCart : shopWords(commerce.language).soldOut}</button>
      </div>
    </div>
  );
};

// Draws the whole panel for one product: pictures, name, price, stock, words, options, and the way to its page.
const QuickViewBody: React.FC<{ product: StorefrontProduct; commerce: Commerce }> = ({ product, commerce }) => {
  const [variant, setVariant] = useState<Variant | undefined>(() => firstOrderable(product));
  const stock = statedStock(variant);
  const words = descriptionWords(product.descriptionHtml);
  return (
    <div className="xe-quick">
      <QuickGallery product={product} />
      <h2 className="xe-quick-title" dir="auto">{product.title}</h2>
      <QuickPrice variant={variant} commerce={commerce} />
      <p className="xe-quick-note">{shopWords(commerce.language).deliveryAtCheckout}</p>
      {stock === null ? null : <p className="xe-quick-stock" data-out={stock > 0 ? undefined : ''}>{stock > 0 ? withCount(shopWords(commerce.language).inStock, stock) : shopWords(commerce.language).outOfStock}</p>}
      {words ? <p className="xe-quick-words" dir="auto">{words}</p> : null}
      <QuickBuy product={product} commerce={commerce} variant={variant} onPick={setVariant} />
      <a className="xe-quick-details" href={`/products/${encodeURIComponent(product.slug)}`}>{shopWords(commerce.language).viewFullDetails}<ChevronRight size={18} aria-hidden="true" /></a>
    </div>
  );
};

// Mounts the quick view for this visit; a card's quick-view button and its "Choose options" open it.
export const QuickView: React.FC = () => {
  const commerce = useCommerce();
  if (!commerce) return null;
  const product = commerce.quick;
  return (
    <CheckoutModal open={product !== null} placement="drawer" title={product ? `Quick view: ${product.title}` : 'Quick view'} closeLabel="Close" onClose={() => commerce.peek(null)}>
      {product ? <QuickViewBody key={product.id} product={product} commerce={commerce} /> : null}
    </CheckoutModal>
  );
};
